import { useEffect, useState } from 'react'
import {Heart, Settings, User, Save} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import CouponCard from '../components/CouponCard'
import Newsletter from '../components/Newsletter'

interface Coupon {
  _id: string
  title: string
  description: string
  brand: string
  location?: string
  code: string
  expiry?: string
  affiliateUrl: string
  discount: string
  featured?: boolean
  clickCount: number
}

interface UserPreference {
  _id?: string
  userId: string
  favoriteCategories?: string[]
  savedCoupons?: string[]
}

export default function ProfilePage() {
  const { user, isAuthenticated } = useAuth()
  const [userPreferences, setUserPreferences] = useState<UserPreference | null>(null)
  const [savedCoupons, setSavedCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const categories = [
    'Glen Ivy Hot Springs',
    'Costco Deals',
    'Glen\'s Army Navy',
    'Mapleglen',
    'Spa & Wellness',
    'Outdoor & Adventure'
  ]

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false)
      return
    }

    fetchUserData()
  }, [user, isAuthenticated])

  const fetchUserData = async () => {
    if (!user) return

    try {
      setLoading(true)

      // Fetch user preferences
      const prefsResult = await lumi.entities.userPreferences.list({
        where: { userId: user.userId }
      })

      const userPrefs = prefsResult.list[0] || null
      setUserPreferences(userPrefs)
      setSelectedCategories(userPrefs?.favoriteCategories || [])

      // Fetch saved coupons
      if (userPrefs?.savedCoupons && userPrefs.savedCoupons.length > 0) {
        const couponsResult = await lumi.entities.coupons.list({
          limit: 100
        })

        const saved = couponsResult.list.filter(coupon =>
          userPrefs.savedCoupons?.includes(coupon._id)
        )
        setSavedCoupons(saved)
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    )
  }

  const savePreferences = async () => {
    if (!user) return

    try {
      if (userPreferences?._id) {
        // Update existing preferences
        await lumi.entities.userPreferences.update(userPreferences._id, {
          favoriteCategories: selectedCategories
        })
      } else {
        // Create new preferences
        await lumi.entities.userPreferences.create({
          userId: user.userId,
          userName: user.userName,
          favoriteCategories: selectedCategories,
          savedCoupons: [],
          createdAt: new Date().toISOString()
        })
      }

      alert('Preferences saved successfully!')
      fetchUserData()
    } catch (error) {
      console.error('Error saving preferences:', error)
      alert('Failed to save preferences')
    }
  }

  const removeSavedCoupon = async (couponId: string) => {
    if (!user || !userPreferences?._id) return

    try {
      const newSaved = (userPreferences.savedCoupons || []).filter(id => id !== couponId)
      
      await lumi.entities.userPreferences.update(userPreferences._id, {
        savedCoupons: newSaved
      })

      setSavedCoupons(prev => prev.filter(c => c._id !== couponId))
      setUserPreferences(prev => ({
        ...prev!,
        savedCoupons: newSaved
      }))
    } catch (error) {
      console.error('Error removing coupon:', error)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Sign in to view your profile</h1>
          <p className="text-gray-600 mb-8">
            Create an account or sign in to save your favorite deals and manage your preferences.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-blue-600 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Welcome, {user?.userName}!</h1>
              <p className="text-gray-600">Manage your deals and preferences</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Preferences Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <Settings className="w-6 h-6 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">Preferences</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Favorite Categories</h3>
                  <div className="space-y-2">
                    {categories.map(category => (
                      <label
                        key={category}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => toggleCategory(category)}
                          className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                        />
                        <span className="text-gray-700">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  onClick={savePreferences}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-blue-700 transition shadow-md hover:shadow-lg"
                >
                  <Save className="w-5 h-5" />
                  Save Preferences
                </button>
              </div>
            </div>
          </div>

          {/* Saved Coupons Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex items-center gap-2 mb-6">
                <Heart className="w-6 h-6 text-red-500 fill-red-500" />
                <h2 className="text-2xl font-bold text-gray-900">Saved Deals</h2>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                </div>
              ) : savedCoupons.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg mb-2">No saved deals yet</p>
                  <p className="text-gray-500">
                    Start saving your favorite deals to access them quickly!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedCoupons.map(coupon => (
                    <div key={coupon._id} className="relative">
                      <button
                        onClick={() => removeSavedCoupon(coupon._id)}
                        className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all"
                        title="Remove from saved"
                      >
                        <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                      </button>
                      <CouponCard {...coupon} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="mt-8">
          <Newsletter />
        </div>
      </div>
    </div>
  )
}